// Manager Approve popup body — renders the shared, config-driven
// FinanceApprovalPanel (type = submissionManager) inside the dynamic popup.
// Admin tags a module's "Approve" action to popupKey "manager-approve".
//
// This backend has NO by-id detail route for submissions/clients/jobs
// (module-data-detail 404s) and its list projections drop the reference IDs,
// so the popup resolves the full picture itself, in-file, from the endpoints
// that DO work:
//   • getFormGroups(submission,id) → recordMeta.{clientId, jobId}
//   • jobs   → getJobDetailView (jobPayDetails = Client Job Rate, jobTitle)
//   • clients   list → clientName (+ vmsFee when the projection exposes it)
//   • employers list → empCompanyName / empCompanyLocation
//   • locations list → taxValue (W2 tax) + name, matched by id OR name
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Drawer, Modal, Radio, Spin, message } from 'antd';
import FinanceApprovalPanel from '../../../finance/FinanceApprovalPanel.jsx';
import { FINANCE_TYPE_SUBMISSION_MANAGER } from '../../../finance/financeApprovalConfig.js';
import { resolveSubmissionFinance, getReferralOptions, idOf } from '../../../finance/financeApprovalData.js';
import { approveSubmission as approveSubmissionViaVms } from '../../../../services/popupApi';
import {
  getApproveMailDraft,
} from '../../../../services/mailApproveApi';
import { sendBulkMail } from '../../../../services/dropdownApi';
import CommonMailComposer from '../../CommonMailComposer.jsx';
import { areValidEmails, parseEmails } from '../../mailUtils.js';

// firstText — first non-blank value. `??` only falls through on null/undefined,
// so an upstream chain that resolves to '' (see submittedBy below) would stop
// dead on it and never reach the fallbacks that exist for exactly that case.
function firstText(...values) {
  return values
    .map((value) => (value === null || value === undefined ? '' : String(value).trim()))
    .find(Boolean) ?? '';
}

const sameText = (a, b) => Boolean(a) && a.toLowerCase() === firstText(b).toLowerCase();

// resolveSubmittedBy — "Submitted By" must name the PERSON who submitted.
// resolveSubmissionFinance's display.submittedBy is a `||` chain that ends in
// `flat.recruiterName || flat.submitSource`, and neither of those is a
// submitter: submitSource is the submission's SOURCE ("Internal", or a vendor)
// — this very function reads it as `vendor` one line down — and recruiterName
// is the ASSIGNED recruiter. Real submissions store submitSource but no
// recruiterName, so display.submittedBy came back as the source string, which
// is truthy, which meant the correct seed.signatureEmail fallback never ran
// and the popup tagged "Internal" as the submitter.
// So: take the resolved value only when it is not one of those source/vendor
// strings, then fall back to the submitter's own signature email.
function resolveSubmittedBy(seed, display, vendor) {
  const resolved = firstText(display.submittedBy);
  const isSourceLeak = sameText(resolved, vendor)
    || sameText(resolved, seed.submitSource)
    || sameText(resolved, seed.supplierName)
    || sameText(resolved, seed.source)
    || sameText(resolved, seed.candidateSource);
  return firstText(isSourceLeak ? '' : resolved, seed.signatureEmail);
}

// Display identity — resolved values (form-groups + joins) win over the
// clicked row, which on the detail surface is flattened and often stale.
function buildIdentity(seed, display = {}) {
  const vendor = display.employerName ?? seed.supplierName ?? seed.submitSource;
  return {
    client: display.clientName ?? seed.clientName,
    role: display.jobTitle ?? seed.jobTitle ?? seed.designation,
    reqId: display.reqId ?? seed.jobClientRefrenceId ?? seed.mspReqId ?? seed.submissionId,
    name: display.candidateName
      || [seed.firstName, seed.lastName].filter(Boolean).join(' ')
      || seed.candidateFullName,
    location: display.jobLocation ?? seed.city ?? seed.currentLocation ?? seed.jobLocation,
    vendor,
    submittedBy: resolveSubmittedBy(seed, display, vendor),
    submittedAt: display.createdAt ?? seed.createdAt,
    email: display.email ?? seed.email,
    employerLocation: display.employerLocation,
    employerName: display.employerName,
  };
}

function scalarObjectId(value) {
  const raw = value?.$oid ?? value?._id?.$oid ?? value?._id ?? value?.id?.$oid ?? value?.id ?? value;
  if (raw === null || raw === undefined || typeof raw === 'object') return '';
  const text = String(raw).trim();
  return /^[a-f\d]{24}$/i.test(text) ? text : '';
}

function resolveJobId(...values) {
  for (const value of values) {
    const id = [
      value?.jobId,
      value?.JobId,
      value?.job_id,
      value?.jobDetails?._id,
      value?.jobDetails?.id,
      value?.job?._id,
      value?.job?.id,
      value?.submission?.jobId,
      value?.submission?.JobId,
      value?.submission?.job_id,
      value?.submission?.jobDetails?._id,
      value?.submission?.jobDetails?.id,
      value?.submission?.job?._id,
      value?.submission?.job?.id,
      value?.recordMeta?.jobId,
      value?.recordMeta?.JobId,
      value?.recordMeta?.job_id,
      value?.submission?.recordMeta?.jobId,
      value?.submission?.recordMeta?.JobId,
      value?.submission?.recordMeta?.job_id,
    ].map(scalarObjectId).find(Boolean);
    if (id) return id;
  }
  return '';
}

// referralPayload — the fields ApproveSubmissionHandler (Be_Auth_DevOps)
// reads to push a new submissions.referralDetails entry. Omitted entirely
// when no referral was attached, so a plain rate-only approve never touches
// that array.
function referralPayload(referral) {
  if (!referral?.referralId) return {};

  // The submission approve API is consumed by more than one backend version.
  // Persist the manager-entered referral snapshot using both the canonical and
  // legacy aliases, and also send the normalized object used by the newer
  // submission handler to append submissions.referralDetails[].
  //
  // IMPORTANT: use the referral passed by FinanceApprovalPanel here. Its
  // referralRate is computed from the CURRENT edit state, so a manager changing
  // 10 -> 15 must persist 15 rather than the referral master's old rate.
  const referralRate = Number(referral.referralRate ?? referral.referalRate ?? 0) || 0;
  const referralType = String(
    referral.referralType ?? referral.referalType ?? 'RECURRING',
  ).trim() || 'RECURRING';
  const referralId = referral.referralId ?? referral.referalId;

  return {
    referralId,
    referalId: referralId,
    referralRate,
    referalRate: referralRate,
    referralType,
    referalType: referralType,
    referralEnabled: true,
    referalEnabled: true,
    isReferralApplicable: true,
    referralDetails: {
      referralId,
      referalId: referralId,
      referralRate,
      referalRate: referralRate,
      referralType,
      referalType: referralType,
      referralEnabled: true,
      referalEnabled: true,
      isReferralApplicable: true,
    },
  };
}

function normalizeDraft(draft = {}) {
  return {
    to: (draft.to ?? draft.To ?? []).map((item) => item.email ?? item.Email ?? item).filter(Boolean).join(', '),
    cc: (draft.cc ?? draft.Cc ?? []).map((item) => item.email ?? item.Email ?? item).filter(Boolean).join(', '),
    bcc: (draft.bcc ?? draft.Bcc ?? []).map((item) => item.email ?? item.Email ?? item).filter(Boolean).join(', '),
    subject: draft.subject ?? draft.Subject ?? '',
    contents: draft.content?.value ?? draft.Content?.Value ?? draft.contents ?? draft.body ?? '',
  };
}

export default function ManagerApproveBody({ context, selectedIds, selectedRecords, moduleName, onClose }) {
  const seed = selectedRecords?.[0] ?? {};
  const submissionId = selectedIds?.[0] ?? idOf(seed);
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referralOptions, setReferralOptions] = useState([]);
  const [approvalChoiceOpen, setApprovalChoiceOpen] = useState(false);
  const [approvalMode, setApprovalMode] = useState('vms');
  const [pendingComputed, setPendingComputed] = useState(null);
  const [pendingReferral, setPendingReferral] = useState(null);
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [mailDrawerOpen, setMailDrawerOpen] = useState(false);
  const [mailLoading, setMailLoading] = useState(false);
  const [mailSending, setMailSending] = useState(false);
  const [mail, setMail] = useState({
    to: '', cc: '', bcc: '', subject: '', contents: '', showCc: false, showBcc: false,
  });

  useEffect(() => {
    let cancelled = false;
    resolveSubmissionFinance(submissionId, seed)
      .then((data) => { if (!cancelled) setResolved(data); })
      .catch((error) => {
        if (cancelled) return;
        message.error(error?.message ?? 'Unable to load finance details');
        setResolved({ record: { submission: seed }, lookups: {}, display: {} });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  // The "Add Referral" section (submissionManager stage now allows Delivery
  // Manager to attach one here too) needs the referral list — was never
  // fetched by this popup, so the dropdown rendered "No data".
  useEffect(() => {
    let cancelled = false;
    getReferralOptions()
      .then((options) => { if (!cancelled) setReferralOptions(options); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const submitApproval = async () => {
    if (!pendingComputed) return;
    if (approvalMode === 'mail') {
      setApprovalChoiceOpen(false);
      setMailDrawerOpen(true);
      setMailLoading(true);
      try {
        const draft = normalizeDraft(await getApproveMailDraft(submissionId));
        setMail({
          to: draft.to,
          cc: draft.cc,
          bcc: draft.bcc,
          subject: draft.subject,
          contents: draft.contents,
          showCc: Boolean(draft.cc),
          showBcc: Boolean(draft.bcc),
        });
      } catch (error) {
        message.error(error?.message ?? 'Unable to prepare approval mail');
      } finally {
        setMailLoading(false);
      }
      return;
    }

    setApprovalSubmitting(true);
    try {
      await approveSubmissionViaVms(submissionId, {
        candidateProposedRate: pendingComputed.proposedRate,
        candidateProposedRateCurrency: pendingComputed.currencyRaw ?? pendingComputed.currency,
        candidateProposedRateUnit: pendingComputed.rateUnit,
        submitVia: 'VMS',
        ...referralPayload(pendingReferral),
      });
      message.success('Submission approved');
      setApprovalChoiceOpen(false);
      setPendingComputed(null);
      setPendingReferral(null);
      onClose?.({ success: true });
    } catch (error) {
      message.error(error?.message ?? 'Unable to approve submission');
    } finally {
      setApprovalSubmitting(false);
    }
  };

  const canSendMail = !mailLoading
    && parseEmails(mail.to).length > 0
    && areValidEmails(mail.to)
    && areValidEmails(mail.cc)
    && areValidEmails(mail.bcc)
    && mail.subject.trim()
    && mail.contents.trim();

  const sendMailAndApprove = async () => {
    if (!pendingComputed || !canSendMail || mailSending) return;
    setMailSending(true);
    const approvalPayload = {
      candidateProposedRate: pendingComputed.proposedRate,
      candidateProposedRateCurrency: pendingComputed.currencyRaw ?? pendingComputed.currency,
      candidateProposedRateUnit: pendingComputed.rateUnit,
    };
    try {
      await approveSubmissionViaVms(submissionId, {
        ...approvalPayload,
        submitVia: 'VMS',
        ...referralPayload(pendingReferral),
      });
      const mailJobId = resolveJobId(resolved?.record, seed);
      if (!mailJobId) {
        message.warning('Submission approved, but job details were not available to send mail');
      } else {
        try {
          await sendBulkMail({
            jobId: mailJobId,
            toMail: parseEmails(mail.to),
            ccMail: parseEmails(mail.cc),
            bccMail: parseEmails(mail.bcc),
            subject: mail.subject,
            contents: mail.contents,
          });
          message.success('Approval mail sent and submission approved');
        } catch (mailError) {
          message.warning(mailError?.message
            ? `Submission approved, but mail was not sent: ${mailError.message}`
            : 'Submission approved, but mail was not sent');
        }
      }
      setMailDrawerOpen(false);
      setPendingComputed(null);
      setPendingReferral(null);
      onClose?.({ success: true });
    } catch (error) {
      message.error(error?.message ?? 'Unable to send approval mail');
    } finally {
      setMailSending(false);
    }
  };

  const actions = useMemo(() => ({
    submissionApprove: async ({ computed, referral }) => {
      // Unit and currency are written back AS RESOLVED, never as literals.
      // This used to send `candidateProposedRateUnit: 'hr'`, so approving a
      // daily or monthly rate silently rewrote it to hourly — the recruiter's
      // choice was destroyed by the act of approving it. computed.rateUnit is
      // the engine's resolution (submission → candidate budget → job → config
      // default) and computed.currencyRaw is the currency exactly as stored,
      // so an ISO code ("USD") is not rewritten into a symbol on approve.
      setPendingComputed(computed);
      // Captured here (not read off `computed`) because the referral the
      // Delivery Manager attached lives in the panel's own local state — see
      // referralPayload() above for what actually gets sent.
      setPendingReferral(referral ?? null);
      setApprovalMode('vms');
      setApprovalChoiceOpen(true);
    },
    // Reject hands off to the dedicated Reject popup (reason dropdown +
    // comments) instead of rejecting inline — config marks this reject with
    // `openPopup: 'reject'`. Swap this popup for that one over the SAME page
    // (replace, so Back returns to the page, not here); carry the selection so
    // the reject popup's own api.submit targets this submission.
    submissionReject: async () => {
      context?.navigate?.('/popup/reject', {
        replace: true,
        state: {
          background: context?.background ?? null,
          selectedIds,
          selectedRecords,
          moduleName,
        },
      });
    },
  }), [context, selectedIds, selectedRecords, moduleName]);

  if (loading || !resolved) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}><Spin size="large" /></div>;
  }

  return (
    <>
      <FinanceApprovalPanel
        type={FINANCE_TYPE_SUBMISSION_MANAGER}
        record={resolved.record}
        lookups={resolved.lookups}
        identity={buildIdentity(seed, resolved.display)}
        referralOptions={referralOptions}
        currentUserId={typeof localStorage !== 'undefined' ? localStorage.getItem('userId') ?? undefined : undefined}
        actions={actions}
        onClose={() => onClose?.()}
        variant="popup"
      />
      <Modal
        title="Choose approval method"
        open={approvalChoiceOpen}
        okText="Approve"
        confirmLoading={approvalSubmitting}
        onOk={submitApproval}
        onCancel={() => {
          if (approvalSubmitting) return;
          setApprovalChoiceOpen(false);
          setPendingComputed(null);
          setPendingReferral(null);
        }}
      >
        <Radio.Group
          value={approvalMode}
          onChange={(event) => setApprovalMode(event.target.value)}
          style={{ display: 'grid', gap: 12 }}
        >
          <Radio value="vms">Via VMS</Radio>
          <Radio value="mail">Via Mail</Radio>
        </Radio.Group>
      </Modal>
      <Drawer
        open={mailDrawerOpen}
        placement="right"
        onClose={() => {
          if (mailSending) return;
          setMailDrawerOpen(false);
        }}
        closable={false}
        rootClassName="mail-popup"
        width={620}
        maskClosable={false}
        footer={(
          <div className="mail-popup__footer">
            <Button onClick={() => setMailDrawerOpen(false)} disabled={mailSending}>
              Cancel
            </Button>
            <Button type="primary" loading={mailSending} disabled={!canSendMail} onClick={sendMailAndApprove}>
              Send
            </Button>
          </div>
        )}
      >
        {mailLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <CommonMailComposer
            value={mail}
            onChange={setMail}
            moduleName="submissions"
            ready={!mailLoading}
            title="Submit Via Mail"
            contextRecords={[resolved.record?.submission ?? seed]}
          />
        )}
      </Drawer>
    </>
  );
}

ManagerApproveBody.propTypes = {
  context: PropTypes.object,
  selectedIds: PropTypes.array,
  selectedRecords: PropTypes.array,
  moduleName: PropTypes.string,
  onClose: PropTypes.func,
};
